// Renders a media attachment thumbnail, pulling from the right places:
// either the local thumbnail (from IndexedDB) or the remote preview (from server after upload)
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getLocalThumbnail } from '../../lib/media_attachments_store';
import { thumbhashToSoftTint } from '../../lib/thumbdata';
import { cn } from '../../lib/utils';

import FilePlaceholderIcon from '../../../assets/images/file-placeholder.svg';

const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  const totalSeconds = Math.round(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return [hours, minutes.toString().padStart(2, '0'), remainingSeconds.toString().padStart(2, '0')].join(':');
  }

  return [minutes.toString().padStart(2, '0'), remainingSeconds.toString().padStart(2, '0')].join(':');
};

const getRemotePreviewUrl = (mediaAttachment) => {
  // Message attachments use medium variant, emojis use emoji variant
  return mediaAttachment?.variants?.medium?.public_url || mediaAttachment?.variants?.emoji?.public_url || null;
};

const getDisplayPreview = (mediaAttachment, localThumbnailUrl) => {
  if (!mediaAttachment) {
    return null;
  }

  // Remote preview (from server after upload)
  const remotePreview = getRemotePreviewUrl(mediaAttachment);

  // Local thumbnail from IndexedDB (640px thumbnail blob)
  const localPreview = localThumbnailUrl;

  return remotePreview || localPreview || null;
};

const MediaAttachmentThumbnail = ({
  mediaAttachment,
  hideDuration = false,
  size = 'size-18',
  className = '',
  rounded = 'rounded-lg',
  showVideoDuration = true,
  showTint = true,
  alt = 'Attachment',
  objectFit = 'contain', // 'contain' (letterbox) or 'cover' (fill and crop)
}) => {
  const [localThumbnailUrl, setLocalThumbnailUrl] = useState(null);
  const [showFallbackIcon, setShowFallbackIcon] = useState(false);

  const remotePreview = getRemotePreviewUrl(mediaAttachment);

  // Load local thumbnail from IndexedDB
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    // Only load if we don't have a remote preview yet
    if (!remotePreview && mediaAttachment?.id) {
      getLocalThumbnail(mediaAttachment.id).then((thumbnailBlob) => {
        if (cancelled || !thumbnailBlob) return;
        objectUrl = URL.createObjectURL(thumbnailBlob);
        setLocalThumbnailUrl(objectUrl);
      });
    } else {
      // Clear local thumbnail when remote is available
      setLocalThumbnailUrl(null);
    }

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaAttachment?.id, remotePreview]);

  const displayPreview = useMemo(() => getDisplayPreview(mediaAttachment, localThumbnailUrl), [mediaAttachment, localThumbnailUrl]);

  const isVideo = Boolean(mediaAttachment?.variants?.mux?.duration);

  const videoDurationLabel = useMemo(() => {
    if (!isVideo || hideDuration) return null;
    return formatDuration(mediaAttachment?.variants?.mux?.duration);
  }, [mediaAttachment?.variants?.mux?.duration, isVideo, hideDuration]);

  // Compute tint style from thumbhash for letterbox background
  const thumbhash = mediaAttachment?.metadata?.thumbhash;
  const tintStyle = useMemo(
    () => (showTint && thumbhash ? { backgroundColor: thumbhashToSoftTint(thumbhash) } : null),
    [showTint, thumbhash],
  );

  // Reset fallback state when preview changes
  useEffect(() => {
    setShowFallbackIcon(false);
  }, [mediaAttachment?.id, displayPreview]);

  const handleImageError = useCallback(() => {
    setShowFallbackIcon(true);
  }, []);

  const containerClasses = cn(
    'relative flex aspect-square items-center justify-center overflow-hidden',
    showTint && 'bg-neutral-100',
    rounded,
    size,
    className,
  );

  const hasPreview = Boolean(displayPreview) && !showFallbackIcon;

  // Video uses background-image for full-bleed thumbnails
  const styleForVideo = hasPreview
    ? {
        backgroundImage: `url(${displayPreview})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    : {};

  // Video render
  if (isVideo) {
    return (
      <div className={containerClasses} style={styleForVideo}>
        {hasPreview && showVideoDuration && videoDurationLabel && (
          <div className="pointer-events-none absolute bottom-2 left-2 rounded-3xl bg-black/50 px-2.5 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
            {videoDurationLabel}
          </div>
        )}
        {!hasPreview && (
          <div className="flex h-full flex-col items-center justify-center">
            <div className="flex max-h-3/4 flex-col items-center justify-center">
              <FilePlaceholderIcon />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Image render
  if (hasPreview) {
    const imageClasses = objectFit === 'cover' ? 'h-full w-full object-cover' : cn('max-h-full max-w-full', showTint && 'bg-white');

    return (
      <div className={containerClasses} style={tintStyle}>
        <img src={displayPreview} alt={alt} className={imageClasses} onError={handleImageError} />
      </div>
    );
  }

  // Fallback icon render
  return (
    <div className={containerClasses}>
      <div className="flex h-full flex-col items-center justify-center">
        <div className="flex max-h-3/4 flex-col items-center justify-center">
          <FilePlaceholderIcon />
        </div>
      </div>
    </div>
  );
};

export default MediaAttachmentThumbnail;
